import { ADMIN_USER_TYPE_KEY } from "@/core/constants";
import { getAuthToken, getCustomClaims } from "@/features/authentication";
import { getAdminOrgId } from "@/shared/utils/admin-org-id";
import { getCookie } from "@/shared/utils/cookies";
import { environment } from "@environment";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/**
 * The headers every backend call needs: the Firebase token plus the admin
 * org / user-type impersonation pair.
 *
 * Lives here, and is exported, because not every call goes through RTK: the
 * story-agent streaming endpoint is a raw `fetch` (see
 * ./story-agent-stream.ts) and `@/lib/axios` backs the rest, and each has to
 * send exactly what `prepareHeaders` sends or it authenticates as somebody
 * else.
 */
export const buildAuthHeaders = async (): Promise<Headers> => {
  const headers = new Headers();

  const authToken = await getAuthToken();
  headers.set("Authorization", authToken);

  // Only an admin token acts in another org (the backend ignores `Orgid`
  // otherwise), and always in the one this tab was opened on.
  const { admin } = await getCustomClaims();
  const orgId = admin ? getAdminOrgId() : "";
  if (orgId) headers.set("Orgid", orgId);

  const adminUserType =
    getCookie(ADMIN_USER_TYPE_KEY) ||
    localStorage.getItem(ADMIN_USER_TYPE_KEY) ||
    "";
  if (adminUserType) headers.set("x-admin-user-type", adminUserType);

  return headers;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: environment.backend,
    prepareHeaders: async (headers) => {
      const auth = await buildAuthHeaders();
      auth.forEach((value, name) => {
        headers.set(name, value);
      });
      return headers;
    },
  }),
  tagTypes: [
    "Version",
    "Versions",
    "Users",
    "Stories",
    "Folders",
    "slides-library",
    "Account",
    "Theme",
    "integrations",
    "slack-integration",
    "stories-folders",
    "fs-variables",
    "VersionSearch",
    "VersionSearchAll",
    "SendingDomain",
    "SendQuota",
  ],
  endpoints: () => ({}),
});
